import { Button, Checkbox, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

import { ShiftListItemDto, ShiftTimeWorkedDtoTypeEnum } from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import {
    calculateOnCallTimeBreakdown,
    getShiftDuration,
    roundToAtMostTwoDecimals,
    toDateISOTimeStr,
} from '@clh/util';

import { ActionButton } from '../../../action-button';
import { useSelection } from '../../../hooks/use-selection';

import { calculateActualClockTimes } from './calculate-actual-clock-times';
import MilitaryTimeInput from './military-time-input';
import { TimeWorkedSummary } from './time-worked-summary';
import { useTimeCalculations } from './use-time-calculations';

dayjs.extend(utc);
dayjs.extend(timezone);

const getFacilityTimezone = (selection: ShiftListItemDto) =>
    selection.facility.address.timezone ?? 'America/Chicago';

const getInitialClockTimes = (selection: ShiftListItemDto) => {
    const facilityTimezone = getFacilityTimezone(selection);
    if (selection.timeWorked?.length) {
        const standardTimeWorked = selection.timeWorked.find(
            (tw) =>
                tw.type === ShiftTimeWorkedDtoTypeEnum.Standard ||
                tw.type === ShiftTimeWorkedDtoTypeEnum.CalledIn
        );
        if (standardTimeWorked) {
            return {
                clockInTime: dayjs(standardTimeWorked.startTime).tz(
                    facilityTimezone
                ),
                clockOutTime: dayjs(standardTimeWorked.endTime).tz(
                    facilityTimezone
                ),
                tookMealBreak: standardTimeWorked.mealBreakMinutes > 0,
            };
        }
    }
    return {
        clockInTime: dayjs(selection.startTime).tz(facilityTimezone),
        clockOutTime: dayjs(selection.endTime).tz(facilityTimezone),
        tookMealBreak: selection.submittedTimeAt
            ? !selection.skippedMealBreak
            : false,
    };
};

const ShiftHeader = ({ selection }: { selection: ShiftListItemDto }) => {
    const facilityTimezone = getFacilityTimezone(selection);
    const shiftDuration = getShiftDuration(
        selection.shiftTime,
        toDateISOTimeStr(selection.startTime),
        toDateISOTimeStr(selection.endTime),
        true
    );

    return (
        <span>
            {DateTime.fromJSDate(selection.startTime, {
                zone: facilityTimezone,
            }).toFormat('MMMM d, yyyy (HHmm')}
            -
            {DateTime.fromJSDate(selection.endTime, {
                zone: facilityTimezone,
            }).toFormat('HHmm)')}{' '}
            ({roundToAtMostTwoDecimals(shiftDuration)}
            hrs)
        </span>
    );
};

const StandardLogTime = ({ close }: { close: () => void }) => {
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const api = useApiClient();

    const initialTimes = getInitialClockTimes(selection);

    const [shiftTimeWorked, setShiftTimeWorked] = useState({
        wasCalledIn: false,
        didNotWork:
            selection.didNotWork !== null ? selection.didNotWork : false,
        tookMealBreak: initialTimes.tookMealBreak,
        clockInTime: initialTimes.clockInTime,
        clockOutTime: initialTimes.clockOutTime,
    });

    const updateTimeWorked = (changes: Partial<typeof shiftTimeWorked>) =>
        setShiftTimeWorked({ ...shiftTimeWorked, ...changes });

    const { worked, onCall } = useTimeCalculations(shiftTimeWorked, selection);

    const facilityTimezone = getFacilityTimezone(selection);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <ShiftHeader selection={selection} />
            <Checkbox
                checked={shiftTimeWorked.didNotWork}
                onChange={() =>
                    updateTimeWorked({
                        didNotWork: !shiftTimeWorked.didNotWork,
                    })
                }
            >
                Did not work
            </Checkbox>
            <Checkbox
                checked={shiftTimeWorked.tookMealBreak}
                disabled={shiftTimeWorked.didNotWork}
                onChange={() =>
                    updateTimeWorked({
                        tookMealBreak: !shiftTimeWorked.tookMealBreak,
                    })
                }
            >
                Took meal break
            </Checkbox>
            Clock in / out
            <Space wrap align="start">
                <MilitaryTimeInput
                    label="Clock-in"
                    value={shiftTimeWorked.clockInTime}
                    disabled={shiftTimeWorked.didNotWork}
                    onTimeChange={(time) =>
                        updateTimeWorked({
                            clockInTime: applyTimeOfDay(
                                shiftTimeWorked.clockInTime,
                                time
                            ),
                        })
                    }
                />
                <MilitaryTimeInput
                    label="Clock-out"
                    value={shiftTimeWorked.clockOutTime}
                    disabled={shiftTimeWorked.didNotWork}
                    onTimeChange={(time) =>
                        updateTimeWorked({
                            clockOutTime: applyTimeOfDay(
                                shiftTimeWorked.clockOutTime,
                                time
                            ),
                        })
                    }
                />
            </Space>
            <TimeWorkedSummary
                worked={worked}
                onCall={onCall}
                tookMealBreak={shiftTimeWorked.tookMealBreak}
                didNotWork={shiftTimeWorked.didNotWork}
            />
            <Space>
                <ActionButton
                    successMessage="Time submitted"
                    action={async () => {
                        let clockInTime: string | undefined;
                        let clockOutTime: string | undefined;

                        if (!shiftTimeWorked.didNotWork) {
                            const scheduledStart = DateTime.fromJSDate(
                                selection.startTime,
                                { zone: facilityTimezone }
                            );
                            const { actualClockIn, actualClockOut } =
                                calculateActualClockTimes(
                                    scheduledStart,
                                    shiftTimeWorked.clockInTime,
                                    shiftTimeWorked.clockOutTime,
                                    facilityTimezone
                                );

                            clockInTime = actualClockIn.toISO() ?? undefined;
                            clockOutTime = actualClockOut.toISO() ?? undefined;
                        }

                        const timeWorked =
                            await api?.shiftControllerSubmitTimeWorked({
                                shiftId: selection.id,
                                submitTimeWorkedDto: {
                                    didNotWork: shiftTimeWorked.didNotWork,
                                    clockEntries:
                                        clockInTime && clockOutTime
                                            ? [
                                                  {
                                                      clockInTime,
                                                      clockOutTime,
                                                      tookMealBreak:
                                                          shiftTimeWorked.tookMealBreak,
                                                  },
                                              ]
                                            : [],
                                },
                            });

                        const { updatedOnCall, updatedWorkedMinutes } = (
                            timeWorked ?? []
                        ).reduce(
                            (acc, segment) => {
                                if (
                                    segment.type ===
                                    ShiftTimeWorkedDtoTypeEnum.OnCall
                                ) {
                                    acc.updatedOnCall += segment.minutesWorked;
                                } else {
                                    acc.updatedWorkedMinutes +=
                                        segment.minutesWorked;
                                }
                                return acc;
                            },
                            { updatedOnCall: 0, updatedWorkedMinutes: 0 }
                        );

                        setSelection({
                            ...selection,
                            timeWorked,
                            minutesWorked: updatedWorkedMinutes,
                            onCallShift: selection.onCallShift
                                ? {
                                      ...selection.onCallShift,
                                      minutesWorked: updatedOnCall,
                                  }
                                : undefined,
                            didNotWork: shiftTimeWorked.didNotWork,
                            skippedMealBreak: !shiftTimeWorked.tookMealBreak,
                        });

                        close();
                    }}
                >
                    Submit
                </ActionButton>
                <Button onClick={close}>Cancel</Button>
            </Space>
        </Space>
    );
};

interface CalledInSegment {
    id: string;
    clockIn: dayjs.Dayjs;
    clockOut: dayjs.Dayjs;
    tookMealBreak: boolean;
}

const newSegmentId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `seg-${Date.now()}-${Math.random()}`;

// The military time inputs emit dayjs values whose date is "today" (the HHMM
// text only carries time-of-day). Preserve the existing dayjs's date so
// validation comparisons against the on-call window stay correct.
// The submit path resolves any midnight-crossing via `calculateActualClockTimes`.
const applyTimeOfDay = (existing: dayjs.Dayjs, picker: dayjs.Dayjs) =>
    existing.hour(picker.hour()).minute(picker.minute()).second(0);

// Recover the on-call window and called-in segments from previously submitted
// timeWorked events. ON_CALL events are clamped to the on-call window, so the
// latest ON_CALL endTime is the best signal for onCallEnd; if no ON_CALL events
// were stored (entire window covered by called-in), fall back to the latest
// event endTime overall.
const getInitialOnCallState = (
    selection: ShiftListItemDto,
    facilityTimezone: string
): {
    onCallStart: dayjs.Dayjs;
    onCallEnd: dayjs.Dayjs;
    calledInSegments: CalledInSegment[];
} => {
    const events = selection.timeWorked ?? [];
    if (events.length === 0) {
        return {
            onCallStart: dayjs(selection.startTime).tz(facilityTimezone),
            onCallEnd: dayjs(selection.endTime).tz(facilityTimezone),
            calledInSegments: [],
        };
    }

    const onCallEvents = events.filter(
        (e) => e.type === ShiftTimeWorkedDtoTypeEnum.OnCall
    );
    const calledInEvents = events.filter(
        (e) => e.type === ShiftTimeWorkedDtoTypeEnum.CalledIn
    );

    const earliestStart = events.reduce((min, e) => {
        const t = new Date(e.startTime).getTime();
        return t < min ? t : min;
    }, Infinity);
    const latestOnCallEnd = onCallEvents.reduce((max, e) => {
        const t = new Date(e.endTime).getTime();
        return t > max ? t : max;
    }, -Infinity);
    const latestEventEnd = events.reduce((max, e) => {
        const t = new Date(e.endTime).getTime();
        return t > max ? t : max;
    }, -Infinity);

    return {
        onCallStart: dayjs(earliestStart).tz(facilityTimezone),
        onCallEnd: dayjs(
            onCallEvents.length > 0 ? latestOnCallEnd : latestEventEnd
        ).tz(facilityTimezone),
        calledInSegments: calledInEvents
            .sort(
                (a, b) =>
                    new Date(a.startTime).getTime() -
                    new Date(b.startTime).getTime()
            )
            .map((e) => ({
                id: newSegmentId(),
                clockIn: dayjs(e.startTime).tz(facilityTimezone),
                clockOut: dayjs(e.endTime).tz(facilityTimezone),
                tookMealBreak: e.mealBreakMinutes > 0,
            })),
    };
};

const OnCallLogTime = ({ close }: { close: () => void }) => {
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const api = useApiClient();

    const facilityTimezone = getFacilityTimezone(selection);
    const initial = useMemo(
        () => getInitialOnCallState(selection, facilityTimezone),
        // Re-derive only when the shift identity changes; subsequent edits to
        // the local state shouldn't reset back to the persisted values.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selection.id]
    );

    const [didNotWork, setDidNotWork] = useState(
        selection.didNotWork !== null ? selection.didNotWork : false
    );
    const [onCallStart, setOnCallStart] = useState<dayjs.Dayjs>(
        initial.onCallStart
    );
    const [onCallEnd, setOnCallEnd] = useState<dayjs.Dayjs>(initial.onCallEnd);
    const [calledInSegments, setCalledInSegments] = useState<CalledInSegment[]>(
        initial.calledInSegments
    );

    const breakdown = useMemo(() => {
        if (didNotWork) {
            return { totalOnCallMinutes: 0, totalCalledInMinutes: 0 };
        }
        return calculateOnCallTimeBreakdown(
            onCallStart.toDate(),
            onCallEnd.toDate(),
            calledInSegments.map((seg) => ({
                clockInTime: seg.clockIn.toDate(),
                clockOutTime: seg.clockOut.toDate(),
                tookMealBreak: seg.tookMealBreak,
            }))
        );
    }, [didNotWork, onCallStart, onCallEnd, calledInSegments]);

    const addSegment = () => {
        const defaultStart = onCallStart.add(1, 'hour');
        const defaultEnd = defaultStart.add(1, 'hour');
        setCalledInSegments([
            ...calledInSegments,
            {
                id: newSegmentId(),
                clockIn: defaultStart,
                clockOut: defaultEnd,
                tookMealBreak: false,
            },
        ]);
    };

    const updateSegment = (id: string, changes: Partial<CalledInSegment>) =>
        setCalledInSegments(
            calledInSegments.map((seg) =>
                seg.id === id ? { ...seg, ...changes } : seg
            )
        );

    const removeSegment = (id: string) =>
        setCalledInSegments(calledInSegments.filter((seg) => seg.id !== id));

    const validation = useMemo(() => {
        const segmentErrors = new Map<string, string[]>();
        let onCallWindowError: string | undefined;

        if (didNotWork) {
            return { onCallWindowError, segmentErrors };
        }

        if (!onCallEnd.isAfter(onCallStart)) {
            onCallWindowError = 'On-call end must be after on-call start.';
        }

        const sortedByClockIn = [...calledInSegments].sort(
            (a, b) => a.clockIn.valueOf() - b.clockIn.valueOf()
        );

        sortedByClockIn.forEach((seg, i) => {
            const errs: string[] = [];
            if (!seg.clockOut.isAfter(seg.clockIn)) {
                errs.push('Clock-out must be after clock-in.');
            }
            if (
                seg.clockIn.isBefore(onCallStart) ||
                !seg.clockIn.isBefore(onCallEnd)
            ) {
                errs.push('Clock-in must fall within the on-call window.');
            }
            if (i > 0) {
                const prev = sortedByClockIn[i - 1];
                if (seg.clockIn.isBefore(prev.clockOut)) {
                    errs.push('Overlaps with another called-in segment.');
                }
            }
            if (errs.length > 0) {
                segmentErrors.set(seg.id, errs);
            }
        });

        return { onCallWindowError, segmentErrors };
    }, [didNotWork, onCallStart, onCallEnd, calledInSegments]);

    const hasErrors =
        !!validation.onCallWindowError || validation.segmentErrors.size > 0;

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <ShiftHeader selection={selection} />
            <Checkbox
                checked={didNotWork}
                onChange={() => setDidNotWork(!didNotWork)}
            >
                Did not work
            </Checkbox>

            {!didNotWork && (
                <>
                    <Typography.Text strong>
                        On call start / end
                    </Typography.Text>
                    <Space wrap align="start">
                        <MilitaryTimeInput
                            label="On-call start"
                            value={onCallStart}
                            hasExternalError={!!validation.onCallWindowError}
                            onTimeChange={(time) =>
                                setOnCallStart(
                                    applyTimeOfDay(onCallStart, time)
                                )
                            }
                        />
                        <MilitaryTimeInput
                            label="On-call end"
                            value={onCallEnd}
                            hasExternalError={!!validation.onCallWindowError}
                            onTimeChange={(time) =>
                                setOnCallEnd(applyTimeOfDay(onCallEnd, time))
                            }
                        />
                    </Space>
                    {validation.onCallWindowError && (
                        <Typography.Text type="danger">
                            {validation.onCallWindowError}
                        </Typography.Text>
                    )}

                    <Typography.Text strong style={{ marginTop: 8 }}>
                        Called-in segments
                    </Typography.Text>
                    {calledInSegments.length === 0 && (
                        <Typography.Text type="secondary">
                            No called-in segments. Add one if the nurse was
                            called in during the on-call period.
                        </Typography.Text>
                    )}
                    {calledInSegments.map((seg, idx) => {
                        const errs = validation.segmentErrors.get(seg.id) ?? [];
                        return (
                            <Space
                                key={seg.id}
                                direction="vertical"
                                style={{ width: '100%' }}
                                size="small"
                            >
                                <Space wrap>
                                    <span>{idx + 1}.</span>
                                    <MilitaryTimeInput
                                        label="Clock-in"
                                        value={seg.clockIn}
                                        hasExternalError={errs.length > 0}
                                        onTimeChange={(time) =>
                                            updateSegment(seg.id, {
                                                clockIn: applyTimeOfDay(
                                                    seg.clockIn,
                                                    time
                                                ),
                                            })
                                        }
                                    />
                                    <MilitaryTimeInput
                                        label="Clock-out"
                                        value={seg.clockOut}
                                        hasExternalError={errs.length > 0}
                                        onTimeChange={(time) =>
                                            updateSegment(seg.id, {
                                                clockOut: applyTimeOfDay(
                                                    seg.clockOut,
                                                    time
                                                ),
                                            })
                                        }
                                    />
                                    <Checkbox
                                        checked={seg.tookMealBreak}
                                        onChange={(e) =>
                                            updateSegment(seg.id, {
                                                tookMealBreak: e.target.checked,
                                            })
                                        }
                                    >
                                        Meal break
                                    </Checkbox>
                                    <Button
                                        type="link"
                                        danger
                                        onClick={() => removeSegment(seg.id)}
                                    >
                                        Remove
                                    </Button>
                                </Space>
                                {errs.map((err) => (
                                    <Typography.Text
                                        key={err}
                                        type="danger"
                                        style={{ marginLeft: 24 }}
                                    >
                                        {err}
                                    </Typography.Text>
                                ))}
                            </Space>
                        );
                    })}
                    <Button onClick={addSegment}>
                        + Add called-in segment
                    </Button>
                </>
            )}

            <TimeWorkedSummary
                worked={breakdown.totalCalledInMinutes / 60}
                onCall={breakdown.totalOnCallMinutes / 60}
                tookMealBreak={false}
                didNotWork={didNotWork}
            />
            <Space>
                <ActionButton
                    successMessage="Time submitted"
                    disabled={hasErrors}
                    action={async () => {
                        const submitTimeWorkedDto: {
                            didNotWork: boolean;
                            onCallStartTime?: string;
                            onCallEndTime?: string;
                            clockEntries: Array<{
                                clockInTime: string;
                                clockOutTime: string;
                                tookMealBreak: boolean;
                            }>;
                        } = {
                            didNotWork,
                            clockEntries: [],
                        };

                        if (!didNotWork) {
                            const scheduledStart = DateTime.fromJSDate(
                                selection.startTime,
                                { zone: facilityTimezone }
                            );

                            const onCallActuals = calculateActualClockTimes(
                                scheduledStart,
                                onCallStart,
                                onCallEnd,
                                facilityTimezone
                            );
                            submitTimeWorkedDto.onCallStartTime =
                                onCallActuals.actualClockIn.toISO() ??
                                undefined;
                            submitTimeWorkedDto.onCallEndTime =
                                onCallActuals.actualClockOut.toISO() ??
                                undefined;

                            submitTimeWorkedDto.clockEntries = calledInSegments
                                .map((seg) => {
                                    const segActuals =
                                        calculateActualClockTimes(
                                            scheduledStart,
                                            seg.clockIn,
                                            seg.clockOut,
                                            facilityTimezone
                                        );
                                    return {
                                        clockInTime:
                                            segActuals.actualClockIn.toISO() ??
                                            '',
                                        clockOutTime:
                                            segActuals.actualClockOut.toISO() ??
                                            '',
                                        tookMealBreak: seg.tookMealBreak,
                                    };
                                })
                                .filter((e) => e.clockInTime && e.clockOutTime);
                        }

                        const timeWorked =
                            await api?.shiftControllerSubmitTimeWorked({
                                shiftId: selection.id,
                                submitTimeWorkedDto,
                            });

                        const { updatedOnCall, updatedWorkedMinutes } = (
                            timeWorked ?? []
                        ).reduce(
                            (acc, segment) => {
                                if (
                                    segment.type ===
                                    ShiftTimeWorkedDtoTypeEnum.OnCall
                                ) {
                                    acc.updatedOnCall += segment.minutesWorked;
                                } else {
                                    acc.updatedWorkedMinutes +=
                                        segment.minutesWorked;
                                }
                                return acc;
                            },
                            { updatedOnCall: 0, updatedWorkedMinutes: 0 }
                        );

                        const anyMealBreak = calledInSegments.some(
                            (seg) => seg.tookMealBreak
                        );

                        setSelection({
                            ...selection,
                            timeWorked,
                            minutesWorked: updatedWorkedMinutes,
                            onCallShift: selection.onCallShift
                                ? {
                                      ...selection.onCallShift,
                                      minutesWorked: updatedOnCall,
                                  }
                                : undefined,
                            didNotWork,
                            skippedMealBreak: !anyMealBreak,
                        });

                        close();
                    }}
                >
                    Submit
                </ActionButton>
                <Button onClick={close}>Cancel</Button>
            </Space>
        </Space>
    );
};

export const LogTime = ({ close }: { close: () => void }) => {
    const { selection } = useSelection<ShiftListItemDto>();

    if (!selection.nurseProfile) {
        return null;
    }

    return selection.onCallShift ? (
        <OnCallLogTime close={close} />
    ) : (
        <StandardLogTime close={close} />
    );
};
